import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { TextService } from "../Services/TextService";

const SITE_ORIGIN = "https://onefront360.com";

const splitLangFromPath = (pathname: string) => {
    const match = pathname.match(/^\/(se|en)(\/.*)?$/);
    if (match) {
        return { lang: match[1], rest: match[2] ?? "/" };
    }
    return { lang: null, rest: pathname || "/" };
};

const setLinkTag = (rel: string, href: string, hrefLang?: string) => {
    const selector = hrefLang ? `link[rel="${rel}"][hreflang="${hrefLang}"]` : `link[rel="${rel}"]`;
    let tag = document.querySelector(selector) as HTMLLinkElement | null;
    if (!tag) {
        tag = document.createElement("link");
        tag.rel = rel;
        if (hrefLang) {
            tag.hreflang = hrefLang;
        }
        document.head.appendChild(tag);
    }
    tag.href = href;
};

type PageSeoProps = {
    pageTitle?: string;
    metaDescription?: string;
    noIndex?: boolean;
};

// Every page route also exists as a bare path ("/x") and a "/:lang/x" path
// ("/se/x", "/en/x") rendering the same component, which Google was indexing
// as separate duplicate URLs. This keeps canonical + hreflang tags in sync
// with whichever variant is currently rendered so search engines treat them
// as language alternates of one page instead of duplicates.
export const usePageSeo = (props: PageSeoProps) => {
    const location = useLocation();

    useEffect(() => {
        document.title = props.pageTitle ?? TextService.GetText("Common_PageTitle");

        let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
        if (!metaDesc) {
            metaDesc = document.createElement("meta");
            metaDesc.name = "description";
            document.head.appendChild(metaDesc);
        }
        metaDesc.content = props.metaDescription ?? "";

        let metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
        if (props.noIndex) {
            if (!metaRobots) {
                metaRobots = document.createElement("meta");
                metaRobots.name = "robots";
                document.head.appendChild(metaRobots);
            }
            metaRobots.content = "noindex, nofollow";
        } else if (metaRobots) {
            metaRobots.remove();
        }

        if (props.noIndex) {
            return;
        }

        const { lang, rest } = splitLangFromPath(location.pathname);
        const sePath = rest === "/" ? "/se/" : `/se${rest}`;
        const enPath = rest === "/" ? "/en/" : `/en${rest}`;
        const canonicalPath = lang === "se" ? location.pathname : enPath;

        setLinkTag("canonical", SITE_ORIGIN + canonicalPath);
        setLinkTag("alternate", SITE_ORIGIN + sePath, "sv");
        setLinkTag("alternate", SITE_ORIGIN + enPath, "en");
        setLinkTag("alternate", SITE_ORIGIN + enPath, "x-default");
    }, [location.pathname, props.pageTitle, props.metaDescription, props.noIndex]);
};
